import { BlockActions } from '@store/blockSaga'
import { thunkDuplicateNode } from '@store/migrationActions/thunkActions/thunkNodesActions'
import { duplicateStickyNote } from '@store/migrationActions/thunkActions/thunkStickyNotesActions'
import { duplicateExternalLink } from '@storeOld/reducers/externalLinks'
import { duplicateSymbol } from '@storeOld/reducers/symbolsLibrary/symbolsLibrary'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { useProjectAccess } from '@/hooks'
import { PAGE_PERMISSION } from '@/permissions/project/definitions'
import type { AppState } from '@/types'
import { Keys } from '../keyboard'

const useOnDuplicateKeyboardActions = () => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const { hasNotAvailableAccess } = useProjectAccess(PAGE_PERMISSION.DUPLICATE)

  // node selectors
  const selectedNode = useSelector<AppState, string | null>(
    ({ canvas }) =>
      canvas.node.selectedNodes[canvas.node.selectedNodes.length - 1]
  )

  const selectedToEditNode = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.node.selectedToEditNode
  )

  // block selectors
  const selectedBlock = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.block.ids[canvas.block.ids.length - 1]
  )

  const selectedToEditBlock = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.block.selectedToEditBlockId
  )

  // sticky note selectors
  const selectedStickyNote = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.stickyNote.selectedStickyNote
  )

  const selectedToEditStickyNote = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.stickyNote.selectedToEditStickyNote
  )

  // external link selectors
  const selectedExternalLink = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.externalLink.selectedExternalLink
  )

  // symbol selectors
  const selectedSymbol = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.symbol.selectedSymbol
  )
  const selectedToEditSymbol = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.symbol.selectedToEditSymbol
  )

  const onDuplicateNode = () => {
    if (selectedNode) {
      dispatch(thunkDuplicateNode(selectedNode))
    }
  }

  const onDuplicateBlock = () => {
    dispatch(BlockActions.duplicateBlock())
  }

  const onDuplicateStickyNote = () => {
    if (!selectedStickyNote) {
      return
    }

    dispatch(duplicateStickyNote({ id: selectedStickyNote }))
  }

  const onDuplicateExternalLink = () => {
    dispatch(duplicateExternalLink())
  }

  const onDuplicateSymbol = () => {
    // @todo implement action
    // @ts-expect-error
    dispatch(duplicateSymbol())
  }

  return (event: KeyboardEvent) => {
    if (
      !(event.ctrlKey || event.metaKey) ||
      !event.shiftKey ||
      event.code !== Keys.D ||
      hasNotAvailableAccess
    ) {
      return
    }

    event.preventDefault()

    if (selectedNode && !selectedToEditNode) {
      onDuplicateNode()
    }

    if (selectedBlock && !selectedToEditBlock) {
      onDuplicateBlock()
    }

    if (selectedStickyNote && !selectedToEditStickyNote) {
      onDuplicateStickyNote()
    }

    if (selectedExternalLink) {
      onDuplicateExternalLink()
    }

    if (selectedSymbol && !selectedToEditSymbol) {
      onDuplicateSymbol()
    }
  }
}

export default useOnDuplicateKeyboardActions
